import React, { useEffect, useRef, useState } from "react";
import { useAdminGuard } from '../AdminOnlyGuard';
import {
  GenericTable,
  type GenericTableColumn,
} from "@/styles/GenericTable";
import { GenericForm, GenericSelect, RequiredMark, type GenericFormField } from "@/styles/GenericForm";
import { SearchButton, ThreeDotMenuButton, StandardButton, NewItemButton } from "@/styles/StandardButtons";
import { PhoneCountryInput } from "@/styles/PhoneCountryInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Trash2, Send } from "lucide-react";
import { useEntityList } from "@/hooks/useEntityList";
import { fetchEntitySummary } from "@/lib/entity-api";
import { useUser } from "@/contexts/UserContext";
import { getApiUrl } from "@/lib/api-config";
import { colors } from "@/design-system/colors";

/**
 * Settings > Users -- every member/admin of this company (the accounts
 * that can sign in and work inside the app), separate from People > Team
 * (a CRM-style profile list that doesn't require login). Clients/vendors
 * are deliberately excluded -- this is account management, not the
 * people directory.
 */

type UserRow = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  role: string;
  is_active: boolean;
  created_at: string;
};

/** What became of the invitation of a person (GET /api/people/invitations); people who were not invited through it have none. */
type InvitationStatus = 'pending' | 'expired' | 'accepted' | 'revoked';
type InvitationInfo = { status: InvitationStatus; last_send_error: string | null };

const ROLE_OPTIONS = [
  { label: 'Admin', value: 'admin' },
  { label: 'Member', value: 'member' },
];

const buildColumns = (invitationOf: (userId: string) => InvitationInfo | undefined): GenericTableColumn<UserRow>[] => [
  {
    key: "name",
    header: "Name",
    sortable: true,
    sortKey: "name",
    width: '14rem',
    render: (row) => (
      <span className="font-medium">{row.first_name} {row.last_name}</span>
    ),
  },
  {
    key: "email",
    header: "Email",
    sortable: true,
    sortKey: "email",
    width: '16rem',
  },
  {
    key: "phone",
    header: "Phone",
    sortable: true,
    sortKey: "phone",
    width: '12rem',
    render: (row) => row.phone || <span className="text-gray-400">—</span>,
  },
  {
    key: "role",
    header: "Role",
    align: "center",
    sortable: true,
    sortKey: "role",
    width: '9rem',
    render: (row) => (
      <span
        className={`
          inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium
          ${row.role === "admin" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}
        `}
      >
        {row.role.charAt(0).toUpperCase() + row.role.slice(1)}
      </span>
    ),
  },
  {
    key: "is_active",
    header: "Status",
    align: "center",
    sortable: true,
    sortKey: "is_active",
    width: '8rem',
    render: (row) => {
      // A person who has not accepted their invitation yet is "Invited" (or "Invitation expired"), not "Active": they cannot sign in until they choose a password.
      const invitation = invitationOf(row.id);
      const label = invitation?.status === 'pending' ? 'Invited' : invitation?.status === 'expired' ? 'Invitation expired' : row.is_active ? 'Active' : 'Inactive';
      const tone = invitation?.status === 'pending' ? 'bg-blue-50 text-blue-700' : invitation?.status === 'expired' ? 'bg-amber-50 text-amber-700' : row.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600';
      return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`}>{label}</span>;
    },
  },
  {
    key: "created_at",
    header: "Joined",
    align: "right",
    width: '10rem',
    sortable: true,
    sortKey: "created_at",
    render: (row) =>
      new Date(row.created_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
  },
];

// The names are GenericForm fields (standard look, two to a row). Email and Role sit in their own row below (UserEmailRoleRow), the phone under them.
const addUserFields: GenericFormField[] = [
  { name: 'first_name', label: 'First Name', type: 'text', required: true, row: 1, colSpan: 1 },
  { name: 'last_name', label: 'Last Name', type: 'text', required: true, row: 1, colSpan: 1 },
];

const userFormFields: GenericFormField[] = [
  { name: 'first_name', label: 'First Name', type: 'text', required: true, row: 1, colSpan: 1 },
  { name: 'last_name', label: 'Last Name', type: 'text', required: true, row: 1, colSpan: 1 },
];

/**
 * The Email / Role row of both user modals: Email takes half of the row and Role a quarter (the last quarter stays free); on a narrow screen they stack.
 * Same field look as every GenericForm field (Label + Input / GenericSelect).
 */
function UserEmailRoleRow({ idPrefix, email, onEmailChange, emailReadOnly, role, onRoleChange, roleDisabled, roleNote }: {
  idPrefix: string;
  email: string;
  onEmailChange?: (value: string) => void;
  emailReadOnly?: boolean;
  role: string;
  onRoleChange: (value: string) => void;
  roleDisabled?: boolean;
  roleNote?: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="email-role-row">
      <div className="space-y-2 md:col-span-2" data-testid="email-field">
        <Label htmlFor={`${idPrefix}-email`} className="font-form-label">Email<RequiredMark required={!emailReadOnly} /></Label>
        <Input id={`${idPrefix}-email`} type="email" className="font-form-input generic-form-field" value={email} readOnly={emailReadOnly} disabled={emailReadOnly}
               onChange={(e) => onEmailChange?.(e.target.value)} />
      </div>
      <div className="space-y-2 md:col-span-1" data-testid="role-field">
        <Label htmlFor={`${idPrefix}-role`} className="font-form-label">Role</Label>
        <GenericSelect id={`${idPrefix}-role`} value={role} required disabled={roleDisabled} options={ROLE_OPTIONS} onValueChange={onRoleChange} />
        {roleNote}
      </div>
    </div>
  );
}

interface AddUserFormState {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
}

const EMPTY_ADD_FORM: AddUserFormState = { first_name: '', last_name: '', email: '', phone: '', role: 'member' };

export function UsersTab() {
  const { companyId, userRole } = useUser();
  const canApply = useAdminGuard();
  // The list: ONE server-driven query (search + sort + page) over the company's admins and members (the same `team_member` list as
  // People > Team) -- the database searches, sorts and pages them (hooks/useEntityList.ts).
  const list = useEntityList<any, UserRow>({
    entityType: "team_member",
    enabled: !!companyId,
    mode: "pages",
    defaultSort: { columnKey: "name", direction: "asc", sortKey: "name" },
    mapRow: (row) => ({
      id: row.user_id,
      first_name: row.user?.first_name ?? "",
      last_name: row.user?.last_name ?? "",
      email: row.user?.email ?? "",
      phone: row.user?.phone ?? null,
      role: row.role,
      is_active: row.is_active ?? true,
      created_at: row.user?.created_at,
    }),
  });
  const isLoading = !list.hasLoaded;
  // The invitations (admins only): who was invited and whether they accepted. A failure to read them never hides the team.
  const [invitations, setInvitations] = useState<Record<string, InvitationInfo>>({});
  const loadInvitations = async () => {
    // the server only answers an admin (403 for anyone else): do not ask on behalf of a member
    if (userRole !== 'admin') return;
    try {
      const response = await fetch(getApiUrl('api/people/invitations'));
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) return;
      setInvitations(Object.fromEntries((result.data as Array<{ user_id: string; status: InvitationStatus; last_send_error: string | null }>).map((i) => [i.user_id, { status: i.status, last_send_error: i.last_send_error }])));
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };
  const columns = React.useMemo(() => buildColumns((id) => invitations[id]), [invitations]);
  // How many ACTIVE admins the company has, counted by the server over everyone (not over the rows on this page): the sole-admin guard.
  const [activeAdminCount, setActiveAdminCount] = useState(0);
  const loadActiveAdminCount = async () => {
    try {
      const summary = await fetchEntitySummary("team_member", { filters: { is_active: "true" } });
      setActiveAdminCount(summary.by.role?.admin?.count ?? 0);
    } catch (error) {
      console.error("Error counting active admins:", error);
    }
  };
  const fetchUsers = async () => {
    list.reload();
    await Promise.all([loadActiveAdminCount(), loadInvitations()]);
  };

  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [role, setRole] = useState('member');
  const [phone, setPhone] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddUserFormState>(EMPTY_ADD_FORM);
  const [addError, setAddError] = useState<string | null>(null);
  const [isInviting, setIsInviting] = useState(false);
  // Set after the invitation is created: whether the email went out (the person chooses their own password from the link; nobody sees a password).
  const [inviteResult, setInviteResult] = useState<{ email: string; emailSent: boolean; sendError: string | null } | null>(null);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // Mirrors GenericForm's own internal formData (first_name/last_name)
  // so this component can compute "unsaved changes" across ALL editable
  // fields, including role/phone, which live outside GenericForm's
  // declarative fields (role is set from its own Select, phone from
  // PhoneCountryInput, both rendered via additionalSections).
  const liveNamesRef = useRef<{ first_name: string; last_name: string }>({ first_name: '', last_name: '' });
  const initialSnapshotRef = useRef<{ first_name: string; last_name: string; role: string; phone: string }>({
    first_name: '', last_name: '', role: 'member', phone: '',
  });

  useEffect(() => {
    if (companyId) { loadActiveAdminCount(); loadInvitations(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userRole]);

  // A company can never be left with zero admins (enforced server-side by
  // enforce_min_one_admin, see migrations/phase60) -- computed here too so
  // the UI can block the action up front with a clear reason, instead of
  // only finding out after a failed request. Same fact covers "the only
  // user of the company": that user is necessarily its only admin.
  const isSoleAdmin = !!selectedUser && selectedUser.role === 'admin' && activeAdminCount <= 1;

  const handleRowClick = (row: UserRow) => {
    setSaveError(null);
    setResendMessage(null);
    setSelectedUser(row);
    setRole(row.role);
    setPhone(row.phone || '');
    initialSnapshotRef.current = {
      first_name: row.first_name,
      last_name: row.last_name,
      role: row.role,
      phone: row.phone || '',
    };
    liveNamesRef.current = { first_name: row.first_name, last_name: row.last_name };
    setIsFormOpen(true);
  };

  const checkDirty = () => {
    const initial = initialSnapshotRef.current;
    return (
      liveNamesRef.current.first_name !== initial.first_name ||
      liveNamesRef.current.last_name !== initial.last_name ||
      role !== initial.role ||
      phone !== initial.phone
    );
  };

  const closeModal = () => {
    setIsFormOpen(false);
    setSelectedUser(null);
    setSaveError(null);
  };

  const handleCancelClick = () => {
    if (checkDirty()) {
      if (window.confirm('Are you sure you want to lose all unsaved progress?')) {
        closeModal();
      }
      return;
    }
    closeModal();
  };

  const handleFormSubmit = async (data: Record<string, any>) => {
    if (!canApply()) return;
    if (!selectedUser) return;
    setIsSaving(true);
    setSaveError(null);

    try {
      // Role first: if this would leave the company with zero admins,
      // fail here before touching the profile fields (see
      // modules/people/service.js's _mapAdminGuardError -- surfaces the
      // DB trigger's exact rejection reason).
      if (role !== selectedUser.role) {
        const roleResponse = await fetch(getApiUrl(`api/user-role/${selectedUser.id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role }),
        });
        if (!roleResponse.ok) {
          const errorData = await roleResponse.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to update role');
        }
      }

      const profileResponse = await fetch(getApiUrl(`api/entities/user/${selectedUser.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: data.first_name,
          last_name: data.last_name,
          phone,
        }),
      });

      if (!profileResponse.ok) {
        const errorData = await profileResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update user');
      }

      closeModal();
      await fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save user. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!canApply()) return;
    if (!selectedUser) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedUser.first_name} ${selectedUser.last_name}? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    setSaveError(null);

    try {
      // Soft delete via soft_delete_user() (see backend/migrations/
      // phase33_user_and_company_soft_delete.sql) -- same endpoint People
      // > Team uses: one atomic call closes the user_company_relation row
      // AND stamps users.deleted_at/deleted_by. Rejected server-side (see
      // isSoleAdmin above) if this is the company's last active admin.
      const response = await fetch(getApiUrl(`api/entities/user/${selectedUser.id}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete user');
      }

      closeModal();
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to delete user. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const openAddModal = () => {
    setAddForm(EMPTY_ADD_FORM);
    setAddError(null);
    setInviteResult(null);
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setAddForm(EMPTY_ADD_FORM);
    setAddError(null);
    setInviteResult(null);
  };

  const addFormDirty = () => !inviteResult && !!(addForm.first_name || addForm.last_name || addForm.email || addForm.phone);

  // Creates the invitation (server-side: the account, a one-time link and the email in KrewUp's template). A second click while it is running does nothing.
  const handleInviteSubmit = async () => {
    if (!canApply()) return;
    if (isInviting) return;
    setIsInviting(true);
    setAddError(null);

    try {
      const response = await fetch(getApiUrl('api/people/invite'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: addForm.first_name.trim(),
          last_name: addForm.last_name.trim(),
          email: addForm.email.trim(),
          phone: addForm.phone || undefined,
          role: addForm.role,
        }),
      });

      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.error || 'Failed to send the invitation');
      }

      setInviteResult({ email: result.data.email, emailSent: result.data.invitation?.email_sent !== false, sendError: result.data.invitation?.send_error ?? null });
      await fetchUsers();
    } catch (error) {
      console.error('Error inviting user:', error);
      setAddError(error instanceof Error ? error.message : 'Failed to send the invitation. Please try again.');
    } finally {
      setIsInviting(false);
    }
  };

  // A new one-time link for a person who has not accepted yet (the previous link stops working)
  const handleResendInvitation = async () => {
    if (!canApply()) return;
    if (!selectedUser || resending) return;
    setResending(true);
    setResendMessage(null);
    setSaveError(null);
    try {
      const response = await fetch(getApiUrl(`api/people/invitations/${selectedUser.id}/resend`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) throw new Error(result?.error || 'Failed to resend the invitation');
      setResendMessage(result.data.sent ? `Invitation sent again to ${selectedUser.email}.` : `The invitation could not be sent: ${result.data.error || 'unknown error'}`);
      await loadInvitations();
    } catch (error) {
      console.error('Error resending the invitation:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to resend the invitation');
    } finally {
      setResending(false);
    }
  };

  const menuItems = [
    {
      label: "Export",
      icon: <Download className="w-4 h-4" />,
      onClick: () => console.log("Export clicked"),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[24rem]">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6 flex items-center justify-end">
        <div className="flex items-center gap-2">
          <NewItemButton label="Add User" onClick={openAddModal} />
          <SearchButton
            onSearch={list.setSearchInput}
            placeholder="Search users..."
          />
          <ThreeDotMenuButton items={menuItems} />
        </div>
      </div>

      <GenericTable
        columns={columns}
        {...list.tableProps}
        rowKey={(row) => row.id}
        scrollContainerMaxWidth="calc(100vw - 19.2rem)"
        minTableWidth={960}
        onRowClick={handleRowClick}
        emptyState="No users found"
      />

      <GenericForm
        title="User"
        mode="edit"
        fields={userFormFields}
        initialValues={selectedUser || {}}
        onSubmit={handleFormSubmit}
        onCancel={closeModal}
        isOpen={isFormOpen}
        error={saveError}
        saving={isSaving}
        hasUnsavedChanges={checkDirty}
        onFieldChange={(name, value) => {
          if (name === 'first_name' || name === 'last_name') {
            liveNamesRef.current = { ...liveNamesRef.current, [name]: value };
          }
        }}
        additionalSections={
          selectedUser && (
            <>
              <UserEmailRoleRow
                idPrefix="user"
                email={selectedUser.email}
                emailReadOnly
                role={role}
                onRoleChange={setRole}
                roleDisabled={isSoleAdmin}
                roleNote={isSoleAdmin ? <p className="mt-1 text-xs text-gray-500">This is the company's only admin. Assign another admin before changing this.</p> : null}
              />
              <div className="space-y-2 md:w-1/2">
                <Label className="font-form-label">Phone</Label>
                <PhoneCountryInput value={phone} onChange={setPhone} />
              </div>
              {invitations[selectedUser.id] && (invitations[selectedUser.id].status === 'pending' || invitations[selectedUser.id].status === 'expired') && (
                <p className="text-sm text-gray-600" data-testid="invitation-state">
                  {invitations[selectedUser.id].status === 'expired' ? 'The invitation expired before it was accepted.' : 'This person has not accepted the invitation yet.'}
                  {invitations[selectedUser.id].last_send_error ? ` The last email could not be sent: ${invitations[selectedUser.id].last_send_error}.` : ''}
                </p>
              )}
              {resendMessage && <p className="text-sm text-gray-700" role="status">{resendMessage}</p>}
            </>
          )
        }
        customFooterButtons={
          <div className="flex w-full items-center justify-between">
            <StandardButton
              type="button"
              customVariant="white"
              onClick={handleDeleteUser}
              disabled={isDeleting || isSaving || isSoleAdmin}
              className="text-red-600 hover:text-red-700"
              title={isSoleAdmin ? "This is the company's only admin -- assign another admin before deleting." : undefined}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {isDeleting ? 'Deleting…' : 'Delete User'}
            </StandardButton>
            <div className="flex gap-2">
              {selectedUser && (invitations[selectedUser.id]?.status === 'pending' || invitations[selectedUser.id]?.status === 'expired') && (
                <StandardButton type="button" customVariant="white" onClick={handleResendInvitation} disabled={resending || isSaving || isDeleting}>
                  <Send className="h-4 w-4 mr-1" />
                  {resending ? 'Sending…' : 'Resend invitation'}
                </StandardButton>
              )}
              <StandardButton type="button" customVariant="white" onClick={handleCancelClick}>
                Cancel
              </StandardButton>
              <StandardButton
                type="submit"
                disabled={isSaving || isDeleting}
                style={{ backgroundColor: colors.buttonSecondary, color: colors.buttonTextWhite, border: 'none' }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </StandardButton>
            </div>
          </div>
        }
      />

      <GenericForm
        title="User"
        mode="create"
        fields={addUserFields}
        initialValues={{ first_name: '', last_name: '', email: '' }}
        onSubmit={handleInviteSubmit}
        onCancel={closeAddModal}
        isOpen={isAddModalOpen}
        error={addError}
        saving={isInviting}
        hasUnsavedChanges={addFormDirty}
        onFieldChange={(name, value) => {
          if (name === 'first_name' || name === 'last_name') setAddForm((f) => ({ ...f, [name]: String(value ?? '') }));
        }}
        additionalSections={
          inviteResult ? (
            <div className="space-y-3" data-testid="invite-result">
              {inviteResult.emailSent ? (
                <p className="text-sm text-gray-700">We sent an invitation to <span className="font-medium">{inviteResult.email}</span>. They will choose their own password from the link in the email.</p>
              ) : (
                <p className="text-sm text-amber-700" role="alert">The account for {inviteResult.email} was created, but the invitation email could not be sent{inviteResult.sendError ? `: ${inviteResult.sendError}` : ''}. Open the person in the list and use "Resend invitation".</p>
              )}
            </div>
          ) : (
            <>
              <UserEmailRoleRow
                idPrefix="add"
                email={addForm.email}
                onEmailChange={(value) => setAddForm((f) => ({ ...f, email: value }))}
                role={addForm.role}
                onRoleChange={(value) => setAddForm((f) => ({ ...f, role: value }))}
              />
              <div className="space-y-2 md:w-1/2">
                <Label className="font-form-label">Phone</Label>
                <PhoneCountryInput value={addForm.phone} onChange={(v) => setAddForm((f) => ({ ...f, phone: v }))} />
              </div>
            </>
          )
        }
        customFooterButtons={
          inviteResult ? (
            <div className="flex w-full items-center justify-end">
              <StandardButton
                type="button"
                onClick={closeAddModal}
                style={{ backgroundColor: colors.buttonSecondary, color: colors.buttonTextWhite, border: 'none' }}
              >
                Done
              </StandardButton>
            </div>
          ) : (
            <div className="flex w-full items-center justify-end gap-2">
              <StandardButton type="button" customVariant="white" onClick={() => { if (!addFormDirty() || window.confirm('Are you sure you want to lose all unsaved progress?')) closeAddModal(); }}>
                Cancel
              </StandardButton>
              <StandardButton
                type="submit"
                disabled={isInviting || !addForm.first_name.trim() || !addForm.last_name.trim() || !addForm.email.trim()}
                style={{ backgroundColor: colors.buttonSecondary, color: colors.buttonTextWhite, border: 'none' }}
              >
                {isInviting ? 'Sending…' : 'Send Invitation'}
              </StandardButton>
            </div>
          )
        }
      />
    </>
  );
}
